<template>
  <div class="mobile-header">
    <div class="header-bg" :class="webpageSetting.webpage_header_image_key && 'has-header-bg'">
      <video
        v-if="isHeaderVideo"
        class="bg-preview"
        :src="previewFile"
        autoplay
        muted
        loop
        playsinline
        webkit-playsinline
      ></video>
      <img v-else class="bg-preview" :src="previewFile" />
      <div v-if="canEdit" class="add-btn" @click="handleAddBtnClick">
        <div class="btn-icon">
          <i class="el-icon-circle-plus"></i>
        </div>
        <div class="btn-label">{{ $t(`${i18nPrefix}.label.modifyPictureOrVideo`) }}</div>
        <div class=btn-placeholder>{{ $t(`${i18nPrefix}.label.suggestUploadHeaderPicture`) }}</div>
        <input ref="fileSelector" class="file-selector" type="file" :accept="acceptHeaderFileTypeList.join(',')" @change="handleFileSelectorChange"/>
      </div>
      <div class="company-logo">
        <img class="logo-preview" :src="$DjangoBaseUrl(company.logo)" />
      </div>
    </div>
    <div class="action-icon-block">
      <span class="icomoon-menu"></span>
      <template v-if="isMobileBrandingPage">
        <span class="icomoon-heart-empty"></span>
        <span class="icomoon-location"></span>
      </template>
    </div>
    <div v-if="canEdit" class="company-info">
      <el-input v-model="webpageSetting.webpage_title" class="company-info-input" :placeholder="$t(`${i18nPrefix}.label.labelCompanyName`)"></el-input>
      <el-input v-model="webpageSetting.webpage_slogan" class="company-info-input" :placeholder="$t(`${i18nPrefix}.label.labelSlogan`)"></el-input>
    </div>
    <div v-if="isPreview" class="company-info preview">
      <div class="company-name">{{ webpageSetting.webpage_title }}</div>
      <div class="slogan">{{ webpageSetting.webpage_slogan }}</div>
    </div>
  </div>
</template>

<script>
import { CUSTOM_LANDING_PAGE_TYPE } from '@/constant/company'
import { uploadFile, getFilePath } from '@/utils/qiniu'

const HEADER_IMAGE_FILE_TYPE_LIST = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif']
const HEADER_VIDEO_FILE_TYPE = 'video/mp4'
const HEADER_VIDEO_MAX_SIZE = 10

export default {
  props: {
    webpageSetting: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    company: {
      type: Object,
      required: true
    },
    isPreview: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      companyName: null,
      slogan: null,
      acceptHeaderFileTypeList: [...HEADER_IMAGE_FILE_TYPE_LIST, HEADER_VIDEO_FILE_TYPE],
      i18nPrefix: 'admin.company.customLandingPage',
      headerBackgroundImgFile: null

    }
  },
  computed: {
    previewFile () {
      if (this.headerBackgroundImgFile) {
        return window.URL.createObjectURL(this.headerBackgroundImgFile)
      }
      return getFilePath(this.webpageSetting.webpage_header_image_key)
    },
    isHeaderVideo () {
      if (this.headerBackgroundImgFile) {
        return this.headerBackgroundImgFile.type === HEADER_VIDEO_FILE_TYPE
      }
      return this.isVideoKey(this.webpageSetting.webpage_header_image_key)
    },
    isMobileBrandingPage () {
      const mobileBrandingPageList = [CUSTOM_LANDING_PAGE_TYPE.COMPANY_INFO, CUSTOM_LANDING_PAGE_TYPE.EMPLOYEE_COMPANY_INFO]
      return mobileBrandingPageList.includes(this.$route.query.page_num)
    }
  },
  created () {

  },
  methods: {
    async submit () {
      const fileKey = this.fileKeyGenerator()
      if (!this.headerBackgroundImgFile) {
        return true
      }
      const uploadMode = this.headerBackgroundImgFile.type === HEADER_VIDEO_FILE_TYPE ? 'job_description_video' : undefined
      await uploadFile(this.headerBackgroundImgFile, fileKey, true, null, uploadMode)
      this.isUploaded = true
      this.webpageSetting.webpage_header_image_key = fileKey
      this.headerBackgroundImgFile = null
      return fileKey
    },
    fileKeyGenerator () {
      return `custom/header_img/${new Date().valueOf()}${this.getFileExtension(this.headerBackgroundImgFile)}`
    },
    getFileExtension (file) {
      const extension = file?.name?.split('.').pop()
      if (extension) {
        return `.${extension.toLowerCase()}`
      }
      return file?.type === HEADER_VIDEO_FILE_TYPE ? '.mp4' : ''
    },
    isVideoKey (key) {
      return Boolean(key && key.toLowerCase().split('?')[0].endsWith('.mp4'))
    },
    handleAddBtnClick () {
      this.$refs.fileSelector.click()
    },
    handleFileSelectorChange () {
      const file = this.$refs.fileSelector.files[0]
      if (!file) {
        return
      }
      if (!this.acceptHeaderFileTypeList.includes(file.type)) {
        this.$message.error({
          showClose: true,
          message: this.$t(`${this.i18nPrefix}.message.wrongHeaderFileType`)
        })
        return true
      }
      if (file.type === HEADER_VIDEO_FILE_TYPE && file.size > HEADER_VIDEO_MAX_SIZE * 1024 * 1024) {
        this.$message.error({
          showClose: true,
          message: this.$t(`${this.i18nPrefix}.message.wrongHeaderVideoFileSize`, { fileSize: HEADER_VIDEO_MAX_SIZE })
        })
        return true
      }
      this.isUploaded = false
      this.headerBackgroundImgFile = file
      this.webpageSetting.webpage_header_image_id = null
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-header {
  margin-top: 20px;

  .header-bg {
    position: relative;
    width: 108%;
    margin: 0 -4%;
    height: 250px;
    background-color: #ccc;

    &.has-header-bg {
      background-color: #fff;
    }

    .bg-preview {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .add-btn {
      position: absolute;
      top: 80px;
      left: 50%;
      transform: translateX(-50%);
      width: 210px;
      height: 80px;
      cursor: pointer;
      text-align: center;
      color: #fff;

      .btn-icon {
        color: #0096cc;
        font-size: 48px;
      }

      .btn-label {
        font-size: 14px;
      }

      .file-selector {
        display: none;
      }
    }

    .company-logo {
      position: absolute;
      left: 50%;
      bottom: -50px;
      transform: translateX(-50%);
      width: 100px;
      height: 100px;
      background-color: #fff;
      overflow: hidden;
      border-radius: 50%;
      z-index: 1;

      .logo-preview {
        width: 100%;
        height: 100%;
      }
    }
  }

  .action-icon-block {
    margin-top: 20px;
    font-size: 25px;
    color: #666;
    display: flex;

    .icomoon-menu {
      margin-right: auto;
    }

    .icomoon-location {
      margin-left: 14px;
    }
  }

  .company-info {
    .company-info-input {
      margin-top: 20px;

      ::v-deep .el-input__inner {
        border: 1px solid #ddd;
        height: 30px;
        line-height: 30px;
        background: #f7f7f7;
        padding: 0 20px;
        font-size: 12px;
      }
    }

    &.preview {
      margin-top: 20px;

      .company-name {
        padding: 0 30px;
        font-size: 26px;
        color: #333;
        font-weight: bold;
        text-align: center;
      }

      .slogan {
        margin-top: 10px;
        padding: 0 30px;
        font-size: 28px;
        color: #999;
        text-align: center;
      }
    }
  }
}
</style>
